import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Select,
  Avatar,
  Typography,
  App as AntdApp,
  Drawer,
  Divider,
  Space,
  Tooltip,
  Card
} from 'antd';
import {
  LogoutOutlined,
  BulbOutlined,
  BulbFilled,
  DownOutlined,
  LeftOutlined,
  AppstoreOutlined,
  CopyOutlined,
  TagOutlined,
  WalletOutlined,
  UserOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import SidebarItem from './SidebarItem';
import { NAVIGATION_ITEMS } from '../../constants/navigation';
import { NotificationBell } from '../NotificationBell';
import HeaderControls from '../../pages/UserPanel/HeaderControls';
import { analyticsPages } from '../../pages/analytics/Analytics';
import { settingsPages } from '../../pages/Settings/Settings';
import { gamificationPages } from '../../pages/Gamification/Gamification';
import { FilterOutlined } from '@ant-design/icons';
import './Sidebar.css';

const { Text } = Typography;

interface SidebarProps {
  theme: 'dark' | 'light';
  toggleTheme: () => void;
  isSuperAdmin: boolean;
  admins: Array<{ id: string; username: string }>;
  adminsLoading: boolean;
  selectedAdminId: string | null;
  setSelectedAdmin: (adminId: string | null) => void;
  panels: Array<{ id: string; name?: string }>;
  user: any;
  panelLoginStatus: any;
  onLogout: () => void;
  onCopyToClipboard: (text: string) => void;
  // Analytics props
  activeAnalyticsTab?: string;
  onAnalyticsTabChange?: (tab: string) => void;
  filtersExpanded?: boolean;
  onToggleFilters?: () => void;
  // Settings props
  activeSettingsTab?: string;
  onSettingsTabChange?: (tab: string) => void;
  // Gamification props
  activeGamificationTab?: string;
  onGamificationTabChange?: (tab: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  theme,
  toggleTheme,
  isSuperAdmin,
  admins,
  adminsLoading,
  selectedAdminId,
  setSelectedAdmin,
  panels,
  user,
  // panelLoginStatus,
  onLogout,
  onCopyToClipboard,
  activeAnalyticsTab,
  onAnalyticsTabChange,
  filtersExpanded = false,
  onToggleFilters,
  activeSettingsTab,
  onSettingsTabChange,
  activeGamificationTab,
  onGamificationTabChange
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { message: contextMessage } = AntdApp.useApp();
  const [showPanelsDrawer, setShowPanelsDrawer] = useState(false);

  // Check if we're in special routes
  const isAnalyticsRoute = location.pathname.includes('/analytics');
  const isSettingsRoute = location.pathname.includes('/settings');
  const isGamificationRoute = location.pathname.includes('/gamification');
  const isOperationsRoute = location.pathname.includes('/operations');
  const isRequestsRoute = location.pathname.includes('/requests');
  const isUsersRoute = location.pathname.includes('/users');
  const isWalletRoute = location.pathname.includes('/wallet');

  // Determine if we're in a section with subsections (only these hide the main navigation)
  const isInSectionWithSubsections = isAnalyticsRoute || isSettingsRoute || isGamificationRoute;

  // Determine if we're in any special section that has a header bar
  const isInSectionWithHeaderBar = isInSectionWithSubsections || isOperationsRoute || isRequestsRoute || isUsersRoute || isWalletRoute;

  // Filter navigation items based on role
  const filteredNavItems = NAVIGATION_ITEMS.filter(item => {
    if (item.superAdminOnly) {
      return isSuperAdmin;
    }
    return true;
  });

  return (
    <aside
      className="sidebar"
      style={{
        width: '240px',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        background: theme === 'dark' ? '#0a0a0a' : '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
      }}
    >
      {/* Header / Logo */}
      <div style={{
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        // Add extra height when in special sections to align with header bar
        ...(isInSectionWithHeaderBar && {
          minHeight: '70px',
        })
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer'
        }} onClick={() => navigate('/user-panel/dashboard')}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontWeight: 'bold',
            fontSize: '20px',
            letterSpacing: '-0.5px',
            color: theme === 'dark' ? '#fff' : '#1e293b'
          }}>
            TRP <span style={{ color: '#dc2626' }}>♠</span>
          </div>
          <div style={{
            fontSize: '10px',
            color: theme === 'dark' ? '#64748b' : '#94a3b8',
            fontWeight: 600,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            marginTop: '2px'
          }}>
            Casino Solutions
          </div>
        </div>
      </div>

      {/* Analytics Section - Show only when in analytics route */}
      {isAnalyticsRoute && (
        <div style={{
          padding: '12px 8px',
        }}>
          {/* Back to Main Menu Button */}
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => navigate('/user-panel/dashboard')}
            style={{
              width: '100%',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              fontWeight: 500,
              height: '36px',
              borderRadius: '0',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
              e.currentTarget.style.color = '#1890ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = theme === 'dark' ? '#e2e8f0' : '#334155';
            }}
          >
            Volver al Menú
          </Button>

          <Text style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme === 'dark' ? '#64748b' : '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginLeft: '8px',
            marginBottom: '8px',
            display: 'block'
          }}>
            Analytics Sections
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', overflowX: 'hidden' }}>
            {analyticsPages.map((page) => {
              const isActive = activeAnalyticsTab === page.key;
              return (
                <div
                  key={page.key}
                  onClick={() => onAnalyticsTabChange && onAnalyticsTabChange(page.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    borderRadius: '0',
                    cursor: 'pointer',
                    background: isActive
                      ? `linear-gradient(135deg, ${page.color}20, ${page.color}10)`
                      : 'transparent',
                    border: isActive
                      ? `1px solid ${page.color}40`
                      : '1px solid transparent',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    minHeight: '32px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '2px',
                      height: '14px',
                      background: page.color,
                      borderRadius: '0',
                    }} />
                  )}
                  <span style={{ color: page.color, fontSize: '24px', display: 'flex', alignItems: 'center' }}>
                    {page.icon}
                  </span>
                  <Text style={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? page.color : (theme === 'dark' ? '#9ca3af' : '#6b7280'),
                    flex: 1,
                    lineHeight: 1.2,
                  }}>
                    {page.label}
                  </Text>
                </div>
              );
            })}
          </div>

          {/* Filtros Button */}
          <div
            onClick={onToggleFilters}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              borderRadius: '6px',
              cursor: 'pointer',
              background: filtersExpanded
                ? 'linear-gradient(135deg, #722ed120, #722ed110)'
                : 'transparent',
              border: filtersExpanded
                ? '1px solid #722ed140'
                : '1px solid transparent',
              marginTop: '6px',
              transition: 'all 0.2s ease',
              minHeight: '32px',
            }}
            onMouseEnter={(e) => {
              if (!filtersExpanded) {
                e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
              }
            }}
            onMouseLeave={(e) => {
              if (!filtersExpanded) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <FilterOutlined style={{ color: '#722ed1', fontSize: '14px' }} />
            <Text style={{
              fontSize: '11px',
              fontWeight: filtersExpanded ? 600 : 500,
              color: filtersExpanded ? '#722ed1' : (theme === 'dark' ? '#9ca3af' : '#6b7280'),
              flex: 1,
              lineHeight: 1.2,
            }}>
              Filtros
            </Text>
          </div>
        </div>
      )}

      {/* Settings Section - Show only when in settings route */}
      {isSettingsRoute && (
        <div style={{
          padding: '12px 8px',
        }}>
          {/* Back to Main Menu Button */}
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => navigate('/user-panel/dashboard')}
            style={{
              width: '100%',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              fontWeight: 500,
              height: '36px',
              borderRadius: '0',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
              e.currentTarget.style.color = '#1890ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = theme === 'dark' ? '#e2e8f0' : '#334155';
            }}
          >
            Volver al Menú
          </Button>

          <Text style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme === 'dark' ? '#64748b' : '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginLeft: '8px',
            marginBottom: '8px',
            display: 'block'
          }}>
            Settings Sections
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', overflowX: 'hidden' }}>
            {settingsPages.map((page) => {
              // Filter superAdmin-only sections
              if (page.superAdminOnly && !isSuperAdmin) {
                return null;
              }

              const isActive = activeSettingsTab === page.key;
              return (
                <div
                  key={page.key}
                  onClick={() => onSettingsTabChange && onSettingsTabChange(page.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    borderRadius: '0',
                    cursor: 'pointer',
                    background: isActive
                      ? `linear-gradient(135deg, ${page.color}20, ${page.color}10)`
                      : 'transparent',
                    border: isActive
                      ? `1px solid ${page.color}40`
                      : '1px solid transparent',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    minHeight: '32px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '2px',
                      height: '14px',
                      background: page.color,
                      borderRadius: '0',
                    }} />
                  )}
                  <span style={{ color: page.color, fontSize: '24px', display: 'flex', alignItems: 'center' }}>
                    {page.icon}
                  </span>
                  <Text style={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? page.color : (theme === 'dark' ? '#9ca3af' : '#6b7280'),
                    flex: 1,
                    lineHeight: 1.2,
                  }}>
                    {page.label}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gamification Section - Show only when in gamification route */}
      {isGamificationRoute && (
        <div style={{
          padding: '12px 8px',
        }}>
          {/* Back to Main Menu Button */}
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => navigate('/user-panel/dashboard')}
            style={{
              width: '100%',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              fontWeight: 500,
              height: '36px',
              borderRadius: '0',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
              e.currentTarget.style.color = '#1890ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = theme === 'dark' ? '#e2e8f0' : '#334155';
            }}
          >
            Volver al Menú
          </Button>

          <Text style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme === 'dark' ? '#64748b' : '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginLeft: '8px',
            marginBottom: '8px',
            display: 'block'
          }}>
            Gamification Sections
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', overflowX: 'hidden' }}>
            {gamificationPages.map((page) => {
              const isActive = activeGamificationTab === page.key;
              return (
                <div
                  key={page.key}
                  onClick={() => onGamificationTabChange && onGamificationTabChange(page.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    borderRadius: '0',
                    cursor: 'pointer',
                    background: isActive
                      ? `linear-gradient(135deg, ${page.color}20, ${page.color}10)`
                      : 'transparent',
                    border: isActive
                      ? `1px solid ${page.color}40`
                      : '1px solid transparent',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    minHeight: '32px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      left: -8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 3,
                      height: 16,
                      background: page.color,
                      borderRadius: '0',
                    }} />
                  )}
                  <span style={{ color: page.color, fontSize: '24px', display: 'flex', alignItems: 'center' }}>
                    {page.icon}
                  </span>
                  <Text style={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? page.color : (theme === 'dark' ? '#9ca3af' : '#6b7280'),
                    flex: 1,
                    lineHeight: 1.2,
                  }}>
                    {page.label}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* View Mode Selector */}
      {isSuperAdmin && (
        <div style={{
          padding: '12px 16px',
          borderBottom: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #f1f5f9'
        }}>
          <label style={{
            fontSize: '10px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#64748b' : '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            display: 'block',
            marginBottom: '6px',
            paddingLeft: '4px'
          }}>
            View Mode
          </label>
          <div style={{ position: 'relative' }}>
            <Select
              value={selectedAdminId || 'ALL'}
              onChange={(value) => setSelectedAdmin(value === 'ALL' ? null : value)}
              loading={adminsLoading}
              style={{ width: '100%' }}
              bordered={false}
              size="small"
              suffixIcon={<DownOutlined style={{ fontSize: '12px', color: theme === 'dark' ? '#64748b' : '#94a3b8' }} />}
              className="view-mode-select"
            >
              <Select.Option value="ALL">All Admins</Select.Option>
              {admins.map(admin => (
                <Select.Option key={admin.id} value={admin.id}>
                  {admin.username}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {/* Main Navigation - Hidden only in sections with subsections */}
      {!isInSectionWithSubsections && (
        <nav style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '4px 0'
        }} className="custom-scrollbar">
          {filteredNavItems.map((item) => {
            const translatedLabel = t(item.translationKey);
            return (
              <SidebarItem
                key={item.id}
                item={{ ...item, label: translatedLabel }}
                isActive={location.pathname === item.key}
                isCollapsed={false}
                onClick={() => navigate(item.key)}
                theme={theme}
              />
            );
          })}
        </nav>
      )}

      {/* Spacer when in sections with subsections to push footer elements down */}
      {isInSectionWithSubsections && (
        <div style={{ flex: 1 }} />
      )}

      {/* Documentation Quick Access */}
      <div
        onClick={() => window.dispatchEvent(new CustomEvent('open-documentation'))}
        style={{
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          borderTop: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #f1f5f9',
          color: theme === 'dark' ? '#9ca3af' : '#6b7280',
          fontSize: '12px',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)';
          e.currentTarget.style.color = theme === 'dark' ? '#d1d5db' : '#374151';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = theme === 'dark' ? '#9ca3af' : '#6b7280';
        }}
      >
        <BookOutlined style={{ fontSize: '13px', color: '#ac4949' }} />
        <span>{t('sidebar.docsAccess', '¡Bienvenido! Accedé a nuestra documentación')}</span>
      </div>

      {/* Action Icons Section */}
      <div style={{
          padding: '12px 16px',
          borderTop: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '4px'
        }}>
          <HeaderControls isMobile={false} />
          <NotificationBell />
          <Button
            type="text"
            size="small"
            icon={<AppstoreOutlined style={{ fontSize: '16px', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }} />}
            onClick={() => setShowPanelsDrawer(true)}
            style={{ padding: '4px' }}
          />
          <Button
            type="text"
            size="small"
            icon={theme === 'dark' ? <BulbFilled style={{ fontSize: '16px', color: '#9ca3af' }} /> : <BulbOutlined style={{ fontSize: '16px', color: '#6b7280' }} />}
            onClick={toggleTheme}
            style={{ padding: '4px' }}
          />
        </div>

      {/* User Profile Section */}
      <div style={{
        padding: '16px',
        background: theme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(241, 245, 249, 1)',
        borderTop: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          justifyContent: 'flex-start'
        }}>
          <div style={{ position: 'relative' }}>
            <Avatar
              size={40}
              style={{
                backgroundColor: '#991b1b',
                borderRadius: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              {user?.username?.charAt(0).toUpperCase() || 'T'}
            </Avatar>
            <div style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              width: '12px',
              height: '12px',
              backgroundColor: '#10b981',
              border: theme === 'dark' ? '2px solid rgba(255, 255, 255, 0.02)' : '2px solid #f1f5f9',
              borderRadius: '50%'
            }} />
          </div>

          <div style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden'
              }}>
                <Text strong style={{
                  fontSize: '13px',
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: theme === 'dark' ? '#e2e8f0' : '#1e293b'
                }}>
                  {user?.username || 'User'}
                </Text>
                <Text style={{
                  fontSize: '10px',
                  color: theme === 'dark' ? '#64748b' : '#94a3b8',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {user?.role || 'User'}
                </Text>
              </div>

          <Button
            danger
            size="small"
            type="text"
            icon={<LogoutOutlined />}
            onClick={onLogout}
            style={{
              borderRadius: '6px',
              transition: 'all 0.2s ease'
            }}
          />
        </div>
      </div>

      {/* Panels & Wallets Drawer */}
      <Drawer
        title={
          <Space>
            <AppstoreOutlined style={{ fontSize: '20px', color: '#ac4949' }} />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>Panels & Wallets</span>
          </Space>
        }
        placement="right"
        open={showPanelsDrawer}
        onClose={() => setShowPanelsDrawer(false)}
        width={480}
        styles={{
          header: {
            padding: '16px 24px',
            borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
            background: theme === 'dark'
              ? 'linear-gradient(to right, #141414, #1f1f1f)'
              : 'linear-gradient(to right, #f9f9f9, #ffffff)'
          },
          body: {
            padding: '24px',
            background: theme === 'dark' ? '#0a0a0a' : '#fafafa'
          }
        }}
      >
        {/* Admin Info Section */}
        <Card
          style={{
            marginBottom: '24px',
            borderRadius: '0',
            background: theme === 'dark'
              ? 'linear-gradient(145deg, #1a1a1a, #0f0f0f)'
              : 'linear-gradient(145deg, #ffffff, #f5f5f5)',
            border: theme === 'dark' ? '1px solid #2a2a2a' : '1px solid #e5e7eb',
            boxShadow: theme === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.3)'
              : '0 4px 12px rgba(0,0,0,0.08)'
          }}
          styles={{
            body: {
              padding: '20px'
            }
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <Avatar
              size={56}
              icon={<UserOutlined />}
              style={{
                backgroundColor: '#ac4949',
                fontSize: '24px'
              }}
            />
            <div style={{ flex: 1 }}>
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  marginBottom: '4px',
                  color: theme === 'dark' ? '#e2e8f0' : '#1e293b'
                }}
              >
                {user?.username || 'Admin'}
              </Typography.Title>
              <Typography.Text
                type="secondary"
                style={{ fontSize: '13px' }}
              >
                {user?.role || 'Administrator'}
              </Typography.Text>
            </div>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <div>
            <Typography.Text
              strong
              style={{
                display: 'block',
                marginBottom: '8px',
                color: theme === 'dark' ? '#94a3b8' : '#64748b',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Admin ID
            </Typography.Text>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              borderRadius: '0',
              background: theme === 'dark' ? '#0a0a0a' : '#f9fafb',
              border: theme === 'dark' ? '1px solid #2a2a2a' : '1px solid #e5e7eb'
            }}>
              <Typography.Text
                code
                style={{
                  flex: 1,
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  color: theme === 'dark' ? '#e2e8f0' : '#1e293b'
                }}
              >
                {user?.id || 'N/A'}
              </Typography.Text>
              <Tooltip title="Copy Admin ID">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    if (user?.id) {
                      onCopyToClipboard(user.id);
                      contextMessage.success('Admin ID copied to clipboard');
                    }
                  }}
                  style={{
                    color: '#ac4949'
                  }}
                />
              </Tooltip>
            </div>
          </div>
        </Card>

        {/* Panels Section */}
        <Card
          style={{
            marginBottom: '24px',
            borderRadius: '0',
            background: theme === 'dark'
              ? 'linear-gradient(145deg, #1a1a1a, #0f0f0f)'
              : 'linear-gradient(145deg, #ffffff, #f5f5f5)',
            border: theme === 'dark' ? '1px solid #2a2a2a' : '1px solid #e5e7eb',
            boxShadow: theme === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.3)'
              : '0 4px 12px rgba(0,0,0,0.08)'
          }}
          styles={{
            body: {
              padding: '20px'
            }
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '0',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TagOutlined style={{ fontSize: '20px', color: '#ffffff' }} />
            </div>
            <div>
              <Typography.Title
                level={5}
                style={{
                  margin: 0,
                  color: theme === 'dark' ? '#e2e8f0' : '#1e293b'
                }}
              >
                Panels
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                {panels.length} panel{panels.length !== 1 ? 's' : ''} configured
              </Typography.Text>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '10px'
          }}>
            {panels.map((panel, idx) => (
              <Tooltip key={panel.id} title={`Click to copy Panel ID: ${panel.id}`}>
                <div
                  onClick={() => {
                    onCopyToClipboard(panel.id);
                    contextMessage.success(`Panel #${idx + 1} ID copied`);
                  }}
                  style={{
                    padding: '16px 8px',
                    textAlign: 'center',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    border: theme === 'dark' ? '2px solid #2a2a2a' : '2px solid #e5e7eb',
                    background: theme === 'dark'
                      ? 'linear-gradient(135deg, #1e293b, #0f172a)'
                      : 'linear-gradient(135deg, #ffffff, #f8fafc)',
                    color: '#667eea',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: theme === 'dark'
                      ? '0 2px 4px rgba(0,0,0,0.3)'
                      : '0 2px 4px rgba(0,0,0,0.08)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = theme === 'dark'
                      ? '0 8px 16px rgba(102,126,234,0.3)'
                      : '0 8px 16px rgba(102,126,234,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme === 'dark' ? '#2a2a2a' : '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = theme === 'dark'
                      ? '0 2px 4px rgba(0,0,0,0.3)'
                      : '0 2px 4px rgba(0,0,0,0.08)';
                  }}
                >
                  #{idx + 1}
                </div>
              </Tooltip>
            ))}
          </div>
        </Card>

        {/* Wallets Section */}
        <Card
          style={{
            borderRadius: '0',
            background: theme === 'dark'
              ? 'linear-gradient(145deg, #1a1a1a, #0f0f0f)'
              : 'linear-gradient(145deg, #ffffff, #f5f5f5)',
            border: theme === 'dark' ? '1px solid #2a2a2a' : '1px solid #e5e7eb',
            boxShadow: theme === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.3)'
              : '0 4px 12px rgba(0,0,0,0.08)'
          }}
          styles={{
            body: {
              padding: '20px'
            }
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '0',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <WalletOutlined style={{ fontSize: '20px', color: '#ffffff' }} />
            </div>
            <div>
              <Typography.Title
                level={5}
                style={{
                  margin: 0,
                  color: theme === 'dark' ? '#e2e8f0' : '#1e293b'
                }}
              >
                Wallets
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                {user?.wallets?.length || 0} wallet{(user?.wallets?.length || 0) !== 1 ? 's' : ''} available
              </Typography.Text>
            </div>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {user?.wallets && user.wallets.length > 0 ? (
              user.wallets.map((wallet: any, idx: number) => (
                <div
                  key={wallet.id}
                  style={{
                    padding: '16px',
                    borderRadius: '0',
                    border: theme === 'dark' ? '2px solid #2a2a2a' : '2px solid #e5e7eb',
                    background: theme === 'dark'
                      ? 'linear-gradient(135deg, #1e293b, #0f172a)'
                      : 'linear-gradient(135deg, #ffffff, #f8fafc)',
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#f5576c';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme === 'dark' ? '#2a2a2a' : '#e5e7eb';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '0',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#ffffff'
                    }}>
                      {idx + 1}
                    </div>
                    <Typography.Text
                      strong
                      style={{
                        fontSize: '15px',
                        color: theme === 'dark' ? '#e2e8f0' : '#1e293b'
                      }}
                    >
                      {wallet.name || `Wallet ${wallet.id}`}
                    </Typography.Text>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingLeft: '44px'
                  }}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Platform:
                    </Typography.Text>
                    <Typography.Text
                      code
                      style={{
                        fontSize: '12px',
                        color: '#f5576c'
                      }}
                    >
                      {wallet.platform || 'N/A'}
                    </Typography.Text>
                  </div>
                </div>
              ))
            ) : (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: theme === 'dark' ? '#64748b' : '#94a3b8',
                borderRadius: '0',
                border: theme === 'dark' ? '2px dashed #2a2a2a' : '2px dashed #e5e7eb',
                background: theme === 'dark' ? '#0a0a0a' : '#f9fafb'
              }}>
                <WalletOutlined style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }} />
                <Typography.Text type="secondary" style={{ display: 'block' }}>
                  No wallets configured
                </Typography.Text>
              </div>
            )}
          </div>
        </Card>
      </Drawer>
    </aside>
  );
};

export default Sidebar;
